/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState, useEffect } from "react";
import ParentSize from "@visx/responsive/lib/components/ParentSize";
import { scaleLinear, scaleBand } from "@visx/scale";
import { LinePath, Line } from "@visx/shape";
import { Group } from "@visx/group";
import { AxisLeft, AxisRight, AxisBottom } from "@visx/axis";
import { curveLinear } from "@visx/curve";
import { IonSpinner } from "@ionic/react";
import {
  getClearTherapySummary,
  ClearTherapyDataPoint,
} from "../../services/graphs";
import { formatDuration } from "../../utils/helpers";

// ── Match ClearTherapyPressureGraph colors exactly ────────────────────────────
const COLORS = {
  neb: "#00BCD4",
  pep: "#FF6B6B",
  osc: "#1565C0",
  pepWithNeb: "#00BCD4", // NEB color for PEP w/ NEB dots
};
const PRESSURE_PEP_COLOR = "#FF6B6B";
const PRESSURE_OSC_COLOR = "#4FC3F7";

const PEP_NEB_DOT_ID = "session-pressure-pep-neb-dots";
const OSC_NEB_DOT_ID = "session-pressure-osc-neb-dots";

const MARGIN = { top: 30, right: 60, bottom: 50, left: 50 };
const CHART_HEIGHT = 240;

const MODES = ["PEP", "OSC", "NEB"] as const;
type Mode = (typeof MODES)[number];

interface ModeEntry {
  mode: Mode;
  duration: number;
  pressure: { min: number; max: number; median: number } | null;
  barColor: string;
  pressureColor: string | null;
}

// ── Inner chart ────────────────────────────────────────────────────────────────
interface InnerProps {
  data: ClearTherapyDataPoint;
  width: number;
}

const ClearSessionPressureChartInner: React.FC<InnerProps> = ({ data, width }) => {
  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  const modeData: ModeEntry[] = useMemo(() => [
    { mode: "PEP", duration: data.avgPepDuration, pressure: data.pepPressure, barColor: COLORS.pep, pressureColor: PRESSURE_PEP_COLOR },
    { mode: "OSC", duration: data.avgOscDuration, pressure: data.oscPressure, barColor: COLORS.osc, pressureColor: PRESSURE_OSC_COLOR },
    { mode: "NEB", duration: data.avgNebDuration, pressure: null,             barColor: COLORS.neb, pressureColor: null },
  ], [data]);

  // PEP w/ NEB overlay duration (from session summary)
  const pepWithNebDuration = Math.min(
    data.summary?.totalPepWithNebTime ?? 0,
    data.avgPepDuration,
  );

  // OSC w/ NEB overlay duration (from session summary)
  const oscWithNebDuration = Math.min(
    data.summary?.totalOscWithNebTime ?? 0,
    data.avgOscDuration,
  );

  // ── Left Y: Duration ────────────────────────────────────────────────────────
  const maxDuration = useMemo(() => {
    const vals = modeData.map(d => d.duration);
    return Math.max(Math.ceil(Math.max(...vals) * 1.2 / 5) * 5, 5);
  }, [modeData]);

  const yDuration = useMemo(() =>
    scaleLinear<number>({
      domain: [0, maxDuration],
      range: [innerHeight, 0],
      nice: true,
    }), [maxDuration, innerHeight]);

  // ── Right Y: Pressure ───────────────────────────────────────────────────────
  const maxPressure = useMemo(() => {
    const vals = modeData.flatMap(d => (d.pressure ? [d.pressure.max] : []));
    return Math.max(Math.ceil(Math.max(...vals, 5) * 1.2 / 5) * 5, 10);
  }, [modeData]);

  const yPressure = useMemo(() =>
    scaleLinear<number>({
      domain: [0, maxPressure],
      range: [innerHeight, 0],
      nice: true,
    }), [maxPressure, innerHeight]);

  // ── X: Band scale for modes ─────────────────────────────────────────────────
  const xScale = useMemo(() =>
    scaleBand<string>({
      domain: [...MODES],
      range: [0, innerWidth],
      padding: 0.35,
    }), [innerWidth]);

  const bandwidth = xScale.bandwidth();
  const barWidth = bandwidth * 0.75;
  const barOffset = (bandwidth - barWidth) / 2;

  // ── Tick helpers ────────────────────────────────────────────────────────────
  const durationTicks = useMemo(() => {
    const step = maxDuration <= 10 ? 1 : maxDuration <= 30 ? 5 : 10;
    return Array.from({ length: Math.floor(maxDuration / step) + 1 }, (_, i) => i * step);
  }, [maxDuration]);

  const pressureTicks = yPressure.ticks(5).map(t => Math.round(t));

  // ── Diamond helper (matches ClearTherapyPressureGraph) ──────────────────────
  const diamond = (cx: number, cy: number, size: number) =>
    `M ${cx},${cy - size} L ${cx + size},${cy} L ${cx},${cy + size} L ${cx - size},${cy} Z`;

  // Pressure data for line path (only PEP + OSC, skip Neb which has null)
  const pressurePoints = modeData.filter(d => d.pressure !== null);

  return (
    <svg width={width} height={CHART_HEIGHT} style={{ overflow: "visible" }}>
      <defs>
        <pattern id={PEP_NEB_DOT_ID} patternUnits="userSpaceOnUse" width="6" height="6">
          <rect width="6" height="6" fill="transparent" />
          <circle cx="3" cy="3" r="1" fill={COLORS.pepWithNeb} opacity="0.75" />
        </pattern>
        <pattern id={OSC_NEB_DOT_ID} patternUnits="userSpaceOnUse" width="6" height="6">
          <rect width="6" height="6" fill="transparent" />
          <circle cx="3" cy="3" r="1" fill={COLORS.neb} opacity="0.75" />
        </pattern>
      </defs>
      <Group left={MARGIN.left} top={MARGIN.top}>

        {/* Grid lines (duration) */}
        {yDuration.ticks(5).map((tick, i) => (
          <Line
            key={`grid-${i}`}
            from={{ x: 0, y: yDuration(tick) }}
            to={{ x: innerWidth, y: yDuration(tick) }}
            stroke="#f0f0f0"
            strokeWidth={1}
          />
        ))}

        {/* ── Duration bars (Left axis) ── */}
        {modeData.map(d => {
          const x0 = (xScale(d.mode) ?? 0) + barOffset;
          const yTop = yDuration(d.duration);
          const barHeight = innerHeight - yTop;
          return (
            <rect
              key={`bar-${d.mode}`}
              x={x0}
              y={yTop}
              width={barWidth}
              height={Math.max(barHeight, 0)}
              fill={d.barColor}
              rx={2}
              opacity={0.85}
            />
          );
        })}

        {/* ── PEP w/ NEB dot overlay (bottom portion of PEP bar) ── */}
        {pepWithNebDuration > 0 && (() => {
          const x0 = (xScale("PEP") ?? 0) + barOffset;
          const dotH = innerHeight - yDuration(pepWithNebDuration);
          return (
            <rect
              x={x0}
              y={yDuration(pepWithNebDuration)}
              width={barWidth}
              height={Math.max(dotH, 0)}
              fill={`url(#${PEP_NEB_DOT_ID})`}
              rx={0}
              pointerEvents="none"
            />
          );
        })()}

        {/* ── OSC w/ NEB dot overlay (bottom portion of OSC bar) ── */}
        {oscWithNebDuration > 0 && (() => {
          const x0 = (xScale("OSC") ?? 0) + barOffset;
          const dotH = innerHeight - yDuration(oscWithNebDuration);
          return (
            <rect
              x={x0}
              y={yDuration(oscWithNebDuration)}
              width={barWidth}
              height={Math.max(dotH, 0)}
              fill={`url(#${OSC_NEB_DOT_ID})`}
              rx={0}
              pointerEvents="none"
            />
          );
        })()}

        {/* ── Pressure error bars + markers (Right axis) ── */}
        {modeData.map(d => {
          if (!d.pressure || !d.pressureColor) return null;
          const cx = (xScale(d.mode) ?? 0) + bandwidth / 2;
          const yMax = yPressure(d.pressure.max);
          const yMin = yPressure(d.pressure.min);
          const yMed = yPressure(d.pressure.median);
          const tickW = 4;
          const color = d.pressureColor;

          return (
            <g key={`pressure-${d.mode}`}>
              {/* Vertical whisker */}
              <Line
                from={{ x: cx, y: yMax }}
                to={{ x: cx, y: yMin }}
                stroke={color}
                strokeWidth={1}
                opacity={0.6}
              />
              {/* Max — filled diamond */}
              <path d={diamond(cx, yMax, 5)} fill={color} stroke="white" strokeWidth={0.5} />
              {/* Min — hollow diamond */}
              <path d={diamond(cx, yMin, 4)} fill="white" stroke={color} strokeWidth={1.5} />
              {/* Median — horizontal line (matches Cough chart) */}
              <Line
                from={{ x: cx - tickW * 2, y: yMed }}
                to={{ x: cx + tickW * 2, y: yMed }}
                stroke={color}
                strokeWidth={2.5}
                strokeLinecap="round"
              />
            </g>
          );
        })}

        {/* Median pressure line connecting PEP → OSC (dashed, like ClearTherapyPressureGraph) */}
        {pressurePoints.length > 1 && (
          <LinePath
            data={pressurePoints}
            x={d => (xScale(d.mode) ?? 0) + bandwidth / 2}
            y={d => yPressure(d.pressure!.median)}
            stroke={PRESSURE_OSC_COLOR}
            strokeWidth={1.5}
            strokeDasharray="6,4"
            curve={curveLinear}
          />
        )}

        {/* ── Axes ── */}
        <AxisBottom
          scale={xScale}
          top={innerHeight}
          stroke="#9ca3af"
          tickStroke="#9ca3af"
          tickLength={0}
          tickFormat={v => v === "NEB" ? "NEB only" : String(v)}
          tickLabelProps={() => ({ fill: "#666", fontSize: 10, textAnchor: "middle", dy: 4 })}
        />

        {/* Left: Duration */}
        <AxisLeft
          scale={yDuration}
          tickValues={durationTicks}
          stroke="#9ca3af"
          tickStroke="#9ca3af"
          tickLength={0}
          tickLabelProps={() => ({ fill: "#666", fontSize: 10, textAnchor: "end", dx: -8 })}
        />

        {/* Right: Pressure */}
        <AxisRight
          left={innerWidth}
          scale={yPressure}
          tickValues={pressureTicks}
          tickFormat={v => String(Math.round(Number(v)))}
          stroke="#333"
          tickStroke="#333"
          tickLength={0}
          tickLabelProps={() => ({ fill: "#333", fontSize: 10, textAnchor: "start", dx: 8 })}
        />

        {/* Right axis label */}
        <text
          transform={`translate(${innerWidth + 50}, ${innerHeight / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize={10}
          fill="#333"
        >
          Pressure (cmH₂O)
        </text>

        {/* Left axis label */}
        <text
          transform={`translate(-38, ${innerHeight / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize={10}
          fill="#666"
        >
          Duration (min)
        </text>

      </Group>
    </svg>
  );
};


// ── Public component ───────────────────────────────────────────────────────────
interface ClearSessionPressureChartProps {
  profileId: number;
  date: string; // "YYYY-MM-DD"
  /** When provided, skips the API fetch and renders this data directly */
  sessionData?: ClearTherapyDataPoint;
}

const ClearSessionPressureChart: React.FC<ClearSessionPressureChartProps> = ({
  profileId,
  date,
  sessionData,
}) => {
  const [data, setData] = useState<ClearTherapyDataPoint | null>(null);
  const [loading, setLoading] = useState(!sessionData);

  useEffect(() => {
    // If session data is provided directly, no need to fetch
    if (sessionData) {
      setData(sessionData);
      setLoading(false);
      return;
    }
    setLoading(true);
    getClearTherapySummary(profileId, date, date)
      .then(res => {
        setData(res.therapyData[0] ?? null);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [profileId, date, sessionData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <IonSpinner name="crescent" className="text-[#00A3E0]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">
        No pressure data available for this session.
      </div>
    );
  }

  return (
    <div>
      {/* Metadata row */}
      {data.summary && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-2">
          {[
            { label: "Mode",     val: data.summary.modes.length ? data.summary.modes.join(" / ") : "—" },
            { label: "NEB",      val: data.summary.nebConnected ? "Connected" : "—" },
            { label: "Total",    val: formatDuration(data.summary.totalDuration) },
            { label: "Phases",   val: String(data.summary.totalPhases) },
          ].map((row) => (
            <span key={row.label} className="text-gray-500">
              {row.label}: <span className="text-gray-800 font-medium">{row.val}</span>
            </span>
          ))}
        </div>
      )}

      {/* Merged legend — duration values + pressure markers in one row */}
      <div className="flex flex-wrap items-center gap-3 text-xs mb-2">
        {/* Duration bars */}
        {[
          { label: "PEP", value: data.summary?.totalPepTime ?? data.avgPepDuration, color: COLORS.pep },
          { label: "OSC", value: data.summary?.totalOscTime ?? data.avgOscDuration, color: COLORS.osc },
          { label: "NEB only", value: data.summary?.totalNebTime ?? data.avgNebDuration, color: COLORS.neb },
        ].map(({ label, value, color }) => (
          <span key={label} className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
            <span className="font-medium" style={{ color }}>{label}</span>
            <span className="text-gray-600">{formatDuration(value)}</span>
          </span>
        ))}

        {/* PEP w/ NEB dot swatch + value */}
        {(data.summary?.totalPepWithNebTime ?? 0) > 0 && (
          <span className="flex items-center gap-1">
            <svg width="13" height="13" viewBox="0 0 13 13">
              <rect x="1" y="1" width="11" height="11" rx="2" fill={COLORS.pep} opacity={0.85} />
              <defs>
                <pattern id="lg-session-pep-neb" patternUnits="userSpaceOnUse" width="4" height="4">
                  <rect width="4" height="4" fill="transparent" />
                  <circle cx="2" cy="2" r="0.7" fill={COLORS.pepWithNeb} opacity="0.8" />
                </pattern>
              </defs>
              <rect x="1" y="1" width="11" height="11" rx="2" fill="url(#lg-session-pep-neb)" />
            </svg>
            <span className="font-medium" style={{ color: COLORS.pep }}>PEP w/ NEB</span>
            <span className="text-gray-600">{formatDuration(data.summary?.totalPepWithNebTime ?? 0)}</span>
          </span>
        )}

        {/* OSC w/ NEB dot swatch + value */}
        {(data.summary?.totalOscWithNebTime ?? 0) > 0 && (
          <span className="flex items-center gap-1">
            <svg width="13" height="13" viewBox="0 0 13 13">
              <rect x="1" y="1" width="11" height="11" rx="2" fill={COLORS.osc} opacity={0.85} />
              <defs>
                <pattern id="lg-session-osc-neb" patternUnits="userSpaceOnUse" width="4" height="4">
                  <rect width="4" height="4" fill="transparent" />
                  <circle cx="2" cy="2" r="0.7" fill={COLORS.neb} opacity="0.8" />
                </pattern>
              </defs>
              <rect x="1" y="1" width="11" height="11" rx="2" fill="url(#lg-session-osc-neb)" />
            </svg>
            <span className="font-medium" style={{ color: COLORS.osc }}>OSC w/ NEB</span>
            <span className="text-gray-600">{formatDuration(data.summary?.totalOscWithNebTime ?? 0)}</span>
          </span>
        )}

        {/* Pressure median markers — shown as horizontal line (matches chart) */}
        {data.pepPressure && (
          <span className="flex items-center gap-1">
            <svg width="20" height="12" viewBox="0 0 20 12">
              <line x1="2" y1="6" x2="18" y2="6" stroke={PRESSURE_PEP_COLOR} strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            <span style={{ color: PRESSURE_PEP_COLOR }}>PEP {data.pepPressure.median} cmH₂O</span>
          </span>
        )}
        {data.oscPressure && (
          <span className="flex items-center gap-1">
            <svg width="20" height="12" viewBox="0 0 20 12">
              <line x1="2" y1="6" x2="18" y2="6" stroke={PRESSURE_OSC_COLOR} strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            <span style={{ color: PRESSURE_OSC_COLOR }}>OSC {data.oscPressure.median} cmH₂O</span>
          </span>
        )}
        {/* Min/max/median hint — matches Cough chart symbols */}
        <span className="text-gray-400 ml-auto text-[10px]">
          <span style={{ fontWeight: 600 }}>◆</span> Max · <span style={{ fontWeight: 600 }}>—</span> Median · <span style={{ fontWeight: 600 }}>◇</span> Min
        </span>
      </div>

      <ParentSize>
        {({ width }) =>
          width > 0 ? (
            <ClearSessionPressureChartInner data={data} width={width} />
          ) : null
        }
      </ParentSize>
    </div>
  );
};

export default ClearSessionPressureChart;
